/**
 * 공지사항 독립 페이지
 * - 기존 useNotices 훅 + NoticeCard 재사용
 * - 카테고리 필터
 * - SEO 최적화
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import { Campaign } from '@mui/icons-material';
import AppBarSection from './components/main/AppBarSection';
import Footer from './components/main/Footer';
import SupportTabNav from './components/common/SupportTabNav';
import { useNotices } from './hooks/useNotices';
import NoticeCard from './components/notice/NoticeCard';
import { categoryStyles } from './components/notice/noticePriorityStyles';
import type { NoticeCategory } from '../types/notice.types';

const CATEGORIES: ('전체' | NoticeCategory)[] = ['전체', '서비스 안내', '시세 변동', '이벤트', '점검 안내'];

export default function NoticesPage() {
  const { activeNotices, loading } = useNotices();
  const [filter, setFilter] = useState<'전체' | NoticeCategory>('전체');

  useEffect(() => {
    document.title = '공지사항 | 다원트레이드';
  }, []);

  const filtered = filter === '전체' ? activeNotices : activeNotices.filter(n => n.category === filter);

  return (
    <Box sx={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <AppBarSection />

      <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 0 }, flex: 1, width: '100%' }}>
        <SupportTabNav active="notices" />

        {/* 타이틀 */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Typography fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, color: '#0f172a', letterSpacing: '-0.02em' }}>
            공지사항
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
            다원트레이드의 최신 소식을 확인하세요
          </Typography>
        </Box>

        {/* 카테고리 필터 */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: { xs: 3, md: 4 }, flexWrap: 'wrap' }}>
          {CATEGORIES.map((cat) => {
            const isActive = filter === cat;
            const style = cat !== '전체' ? categoryStyles[cat as NoticeCategory] : null;
            return (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setFilter(cat)}
                icon={style ? <span style={{ fontSize: '0.8rem' }}>{style.icon}</span> : undefined}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  px: 1,
                  ...(isActive
                    ? { bgcolor: '#0f172a', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                    : { bgcolor: '#f1f5f9', color: '#64748b', '&:hover': { bgcolor: '#e2e8f0' } }),
                  transition: 'all 0.2s',
                }}
              />
            );
          })}
        </Box>

        {/* 공지 목록 */}
        <Box sx={{ mb: 6 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 2 }} />)}
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Campaign sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ color: '#94a3b8' }}>
                {filter === '전체' ? '등록된 공지사항이 없습니다' : `'${filter}' 카테고리의 공지사항이 없습니다`}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filtered.map((notice, idx) => (
                <NoticeCard key={notice.id} notice={notice} index={idx} defaultExpanded={idx === 0} />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
